import type { CivilDateProto, CivilDateRangeProto } from "@noom/noom-contracts/noom_contracts/lib/time/civil_time";
import { format, isValid, parseISO } from "date-fns";

export const formatLocalDateTime = (date: Date): string =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss");

export const getCurrentLocalDateTime = (): string => formatLocalDateTime(new Date());

const dateOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
};

// Utility function to format date range
// Convert CivilDateProto to JavaScript Date
export const civilDateToDate = (date: CivilDateProto): Date => {
  const year = date.year ?? 0;
  const month = date.month ?? 1;
  const day = date.day ?? 1;
  return new Date(year, month - 1, day);
};

const formatDateString = (
  date: string | null | undefined,
  dateFormat: string
): string => {
  if (!date) {
    return "";
  }

  const parsedDate = parseISO(date);

  if (!isValid(parsedDate)) {
    return "";
  }

  return format(parsedDate, dateFormat);
};

type MaybeDateString = string | null | undefined;

export const formatDateMonthDay = (date: MaybeDateString): string =>
  formatDateString(date, "MM/dd");

export const formatDateMonthDayYear = (date: MaybeDateString): string =>
  formatDateString(date, "M/d/yy");

/** e.g. "May 7, 2026" — Care Tab OTC purchase subtitle. */
export const formatDateLongMonthDayYear = (date: MaybeDateString): string =>
  formatDateString(date, "MMMM d, yyyy");

export const formatDateWeekdayMonthDay = (date: MaybeDateString): string =>
  formatDateString(date, "EEEE, MMM d");

// Utility function to format date range
export const formatDeliveryWindow = (range: CivilDateRangeProto): string => {
  if (!range.startDate || !range.endDate) {
    return "Unable to retrieve delivery window";
  }

  const startYear = range.startDate.year ?? 0;
  const startMonth = range.startDate.month ?? 1;
  const startDay = range.startDate.day ?? 1;
  const endYear = range.endDate.year ?? 0;
  const endMonth = range.endDate.month ?? 1;
  const endDay = range.endDate.day ?? 1;

  const startMonthName = new Date(startYear, startMonth - 1, startDay).toLocaleDateString('en-US', { month: 'long' });
  const endMonthName = new Date(endYear, endMonth - 1, endDay).toLocaleDateString('en-US', { month: 'long' });

  if (startMonthName === endMonthName) {
    return `${startMonthName} ${startDay}-${endDay}, ${startYear}`;
  } else {
    return `${startMonthName} ${startDay} - ${endMonthName} ${endDay}, ${startYear}`;
  }
};

/** Format a GraphQL LocalDate (`yyyy-MM-dd`) for the shipping page refill line. */
export const formatShippingLocalDate = (date: string): string => {
  const parsedDate = parseISO(date);
  if (!isValid(parsedDate)) {
    return "Unable to retrieve date";
  }
  return parsedDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Format a GraphQL LocalDateRange for the estimated delivery window. */
export const formatLocalDateDeliveryWindow = (range: {
  startDate: string;
  endDate: string;
}): string => {
  const start = parseISO(range.startDate);
  const end = parseISO(range.endDate);
  if (!isValid(start) || !isValid(end)) {
    return "Unable to retrieve delivery window";
  }

  const startMonthName = start.toLocaleDateString("en-US", { month: "long" });
  const endMonthName = end.toLocaleDateString("en-US", { month: "long" });
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startYear = start.getFullYear();

  if (startMonthName === endMonthName) {
    return `${startMonthName} ${startDay}-${endDay}, ${startYear}`;
  }
  return `${startMonthName} ${startDay} - ${endMonthName} ${endDay}, ${end.getFullYear()}`;
};

export default dateOptions;

